import { MESSAGE_ARTIFACT_REPORT } from '@/shared/config';
import { checkIcorDevice } from '@/entities/device';
import {
  zeroPad,
  generateFullName,
  dateToString,
  gettextReportType,
  getttextPriority,
} from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import { checkNewNotiEventReport } from '@/pages/inbox-details/@x/inbox-list';

/**
 * Builds the badge list rendered on an inbox report row. Always emits a clickable zero-padded study id chip that opens the study information drawer, adds a report type chip on the All tab mapping Study to End of Use and otherwise falling back to the translated type, then appends the translated priority with red or orange styling for Emergent and Urgent, plus Biodirect and iCOR chips when the study qualifies.
 *
 * @param report - Inbox report row to label.
 * @param toggleDisplayDrawer - Callback opening the study information drawer.
 * @param type - Active inbox tab name.
 * @returns The label descriptors for the row.
 */
const generateLabels = (report: any, toggleDisplayDrawer: any, type: string) => {
  const labels: {
    text: string;
    className: string;
    isTextButton?: boolean;
    handleClick?: (e: any) => void;
  }[] = [
    {
      text: `${t`Study`} ${zeroPad(report.studyFid)}`,
      className: '--study-fid',
      isTextButton: true,
      handleClick: (e) => {
        e.stopPropagation();
        toggleDisplayDrawer(report, t`Study information`);
      },
    },
  ];
  if (type === 'All') {
    labels.push({
      text:
        report.type === 'Study'
          ? t`End of Use`
          : report.type === 'Interim'
            ? t`Interim`
            : report.type === 'Notification'
              ? t`Notification`
              : gettextReportType(report.type),
      className: '--report-type',
    });
  }

  labels.push({
    text: getttextPriority(report.priority),
    className: classNames(
      '--priority',
      report.priority === 'Emergent' ? '--red' : report.priority === 'Urgent' ? '--orange' : '',
    ),
  });

  if (report.isBiofluxDirect) {
    labels.push({
      text: t`Biodirect`,
      className: '--biodirect',
    });
  }
  if (checkIcorDevice(report.study?.device)) {
    labels.push({
      text: t`iCOR`,
      className: '--icor-device',
    });
  }
  return labels;
};

/**
 * Flattens raw inbox report records into the row shape the inbox list table renders. Spreads each report while lifting nested study, patient, physician, event and facility fields, builds full names, derives the event time and a start-to-stop date-of-service range via dateToString with the study timezone and offset, and picks comment text from the artifact message, new notification-event findings, joined event comments or technician comments by report type and format version.
 *
 * @param inboxes - Raw inbox report records from the API.
 * @returns The formatted inbox rows.
 */
const inboxesFormatter = (inboxes: any) =>
  _.map(inboxes, (report) => {
    const { isNewFormat } = report;
    const newEventNotiReport = checkNewNotiEventReport(report);
    return {
      ...report,
      id: report.id,
      reportId: report.reportId,
      type: report.type,
      start: report.start,
      stop: report.stop,
      studyFid: report.study?.friendlyId,
      physicianId: report.study?.info?.interprettingPhysician?.id,
      interprettingPhysicianName: generateFullName(report.study?.info?.interprettingPhysician),
      patientName: generateFullName(report.study?.info?.patient),
      patientId: report.study?.info?.patient?.patientId,
      study: report.study,
      studyType: report.study?.studyType,
      timezone: report.study?.timezone,
      eventFid: report.event?.friendlyId,
      physicianInterpretation: report.physicianInterpretation,
      technicianComments: report.isArtifactReport
        ? MESSAGE_ARTIFACT_REPORT
        : newEventNotiReport
          ? report?.reportFindings
          : report.type === 'Event'
            ? _.join(report.event?.comments, ' ')
            : isNewFormat
              ? (report.findings ?? report.technicianComments)
              : report.technicianComments,
      date: report.inbox?.date,
      priority: report.inbox?.priority,
      isReviewed: report.inbox?.isReviewed,
      isRead: report.inbox?.isRead,
      eventTimeUTC: report.start,
      eventTime: dateToString({
        startStudyDate: report.study.start,
        isoDate: report.start,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        facilityCountry: report.study?.facility?.contact?.country,
        facilityTimezone: report.study?.facility?.timezone,
      }),
      dateOfService: `${dateToString({
        isoDate: report.start,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        format: 'MM/DD/YY',
      })} - ${dateToString({
        isoDate: report.stop,
        timezone: report.study?.timezone,
        studyTimeOffset: report.study?.studyTimeOffset,
        format: 'MM/DD/YY',
      })}`,
      eventId: report.event?.id,
      hasClinicData: report.event?.hasClinicData,
      isNotificationReport: report.type === 'Notification',
      isBiofluxDirect: report.study?.isBiofluxDirect,
      hasSignature: report.hasSignature,
      isArtifactReport: report.isArtifactReport,
      facility: report.study?.facility,
      facilityName: report.study?.facility?.name,
      isNewFormat,
      formatVersion: report.formatVersion,
      aiFormatVersion: report.aiFormatVersion,
      statusReport: report.statusReport,
      lastStatusReportAt: report.lastStatusReportAt,
      isSavingStatus: false,
      profile: report.profile,
      status: report.status,
      isNonPhi: report.study?.isNonPhi,
      lastModified: report.lastModified,
      isRenderingBackground: report.isRenderingBackground,
    };
  });
export { generateLabels, inboxesFormatter };
